import { ActionType, Resource, ResourceType } from '@/domain/types/rbac';

/**
 * Configuración de recursos del sistema
 * Define todos los recursos disponibles y sus acciones permitidas
 */

// Acciones estándar para contenido
const CONTENT_ACTIONS: ActionType[] = ['read', 'create', 'update', 'delete'];

// Acciones para gestión administrativa
const ADMIN_ACTIONS: ActionType[] = [
  'read',
  'create',
  'update',
  'delete',
  'manage',
];

// Definición de todos los recursos del sistema
export const SYSTEM_RESOURCES: Resource[] = [
  {
    id: 'newsletters',
    name: 'Publicaciones',
    description: 'Gestión de publicaciones',
    actions: CONTENT_ACTIONS,
  },
  {
    id: 'policies',
    name: 'Políticas',
    description: 'Gestión de políticas corporativas',
    actions: CONTENT_ACTIONS,
  },
  {
    id: 'reports',
    name: 'Reportes',
    description: 'Gestión de reportes y documentos técnicos',
    actions: CONTENT_ACTIONS,
  },
  {
    id: 'releases',
    name: 'Última Hora',
    description: 'Gestión de comunicados de última hora',
    actions: CONTENT_ACTIONS,
  },
  {
    id: 'critical_standars',
    name: 'Estándares Críticos',
    description: 'Gestión de estándares críticos de seguridad',
    actions: CONTENT_ACTIONS,
  },
  {
    id: 'podcasts',
    name: 'Audios',
    description: 'Gestión de contenido de audio y podcasts',
    actions: CONTENT_ACTIONS,
  },
  {
    id: 'videos',
    name: 'Videos',
    description: 'Gestión de contenido de video',
    actions: CONTENT_ACTIONS,
  },
  {
    id: 'links',
    name: 'Enlaces',
    description: 'Gestión de enlaces y recursos externos',
    actions: CONTENT_ACTIONS,
  },
  {
    id: 'galleries',
    name: 'Galerías',
    description: 'Gestión de galerías de imágenes',
    actions: CONTENT_ACTIONS,
  },
  {
    id: 'banners',
    name: 'Banners',
    description: 'Gestión de banners destacados del sistema',
    actions: CONTENT_ACTIONS,
  },
  {
    id: 'users',
    name: 'Usuarios',
    description: 'Gestión de usuarios del sistema',
    actions: ADMIN_ACTIONS,
  },
  {
    id: 'admin_users',
    name: 'Usuarios Administrativos',
    description: 'Gestión de usuarios administrativos y accesos',
    actions: ADMIN_ACTIONS,
  },
  {
    id: 'admin_logs',
    name: 'Logs de Administración',
    description: 'Consulta de logs de acciones administrativas',
    actions: ['read'],
  },
  {
    id: 'roles',
    name: 'Gestión de Roles',
    description: 'Administración de roles y permisos del sistema',
    actions: ADMIN_ACTIONS,
  },
  {
    id: 'campaigns',
    name: 'Campañas',
    description: 'Gestión de campañas de hashtags y rankings',
    actions: CONTENT_ACTIONS,
  },
  {
    id: 'audiences',
    name: 'Audiencias',
    description: 'Gestión de audiencias de publicación',
    actions: ADMIN_ACTIONS,
  },
  {
    id: 'settings',
    name: 'Configuración',
    description: 'Configuración general de la plataforma',
    actions: ADMIN_ACTIONS,
  },
  {
    id: 'self_registered_users',
    name: 'Usuarios Auto-registrados',
    description: 'Gestión de usuarios auto-registrados desde la app móvil',
    actions: ADMIN_ACTIONS,
  },
];

// Mapeo rápido de recursos por ID
export const RESOURCES_MAP = SYSTEM_RESOURCES.reduce(
  (map, resource) => {
    map[resource.id] = resource;
    return map;
  },
  {} as Record<ResourceType, Resource>,
);

// Obtener recurso por ID
export const getResource = (id: ResourceType): Resource | undefined => {
  return RESOURCES_MAP[id];
};

// Verificar si una acción es válida para un recurso
export const isValidAction = (
  resourceId: ResourceType,
  action: ActionType,
): boolean => {
  const resource = getResource(resourceId);
  return resource ? resource.actions.includes(action) : false;
};
